import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import { renewIdToken } from '../../api/session.service';
import { getTraceId } from './traceId';
import { sessionHeaders } from '../../utils/sessionIds';

type FetchQuery = ReturnType<typeof fetchBaseQuery>;

// A 401 usually means the id_token expired. Renew it once and retry the request, so that
// unauthorizedRedirectMiddleware only logs out on a 401 the renewal could not fix.
export const withSessionRenewal =
  (query: FetchQuery): FetchQuery =>
  async (args, api, extraOptions) => {
    const sentToken = localStorage.getItem('id_token');
    const result = await query(args, api, extraOptions);
    const currentToken = localStorage.getItem('id_token');
    if (result.error?.status !== 401 || !sentToken || !currentToken) {
      return result;
    }

    // Another request may already have renewed the token this one was sent with.
    const token =
      currentToken !== sentToken ? currentToken : await renewIdToken();
    return token ? query(args, api, extraOptions) : result;
  };

export const baseQuery = withSessionRenewal(
  fetchBaseQuery({
    baseUrl: import.meta.env.REACT_APP_API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('id_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      const segmentTraceId = getTraceId();
      if (segmentTraceId) {
        headers.set('x-trace-id', segmentTraceId);
      }

      // The device and visit, so the request log can rebuild sessions (identity-and-funnel.md §8)
      Object.entries(sessionHeaders()).forEach(([name, value]) =>
        headers.set(name, value)
      );

      return headers;
    },
  })
);
